import { useState, useContext } from 'react';
import { Modal, ModalDialog, Typography, Button, Stack, Box, Textarea, Checkbox } from '@mui/joy';
import { Star, ChatCircleText } from "@phosphor-icons/react";
import { LayoutContext } from '../../context';
import { APISubmitPaidFeedback } from '../../api';

// Окно сбора отзывов у платных пользователей.
// Тон — НЕ первое знакомство, а продолжение: мы знаем, что человек уже
// какое-то время пользуется APEX, и хотим обратную связь для улучшения.
// Показывается (из App.js) пока user.paid_feedback_prompt === true.
// «Позже» закрывает на сессию; отправка снимает флаг навсегда.
export default function PaidFeedbackModal({ open, onClose }) {
    const { token, updateUser } = useContext(LayoutContext);
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [text, setText] = useState('');
    const [canPublish, setCanPublish] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        if (saving || rating < 1) return;
        setSaving(true);
        setError('');
        try {
            await APISubmitPaidFeedback(token, {
                rating,
                text: text.trim(),
                can_publish: canPublish,
            });
            // Success only: clear the backend prompt flag and close.
            updateUser({ paid_feedback_prompt: false });
            setSaving(false);
            onClose();
        } catch (e) {
            // Do NOT close on failure — otherwise the review is silently lost and
            // the popup keeps returning on next load. Surface it and let her retry.
            console.error('Paid feedback submit error:', e);
            setError('Не удалось отправить отзыв — проверь соединение и попробуй ещё раз.');
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog
                sx={{
                    maxWidth: 460,
                    width: '90vw',
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 'xl',
                }}
            >
                <Box sx={{ mb: 1.5 }}>
                    <ChatCircleText size={46} color="#DA7756" weight="duotone" />
                </Box>

                <Typography level="h3" sx={{ mb: 1 }}>
                    Помоги сделать APEX лучше
                </Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
                    Ты уже какое-то время готовишься к USMLE вместе с APEX — спасибо, что ты с нами.
                    Именно поэтому твоё мнение для нас особенно ценно: расскажи, что работает хорошо,
                    а что стоит улучшить. Это займёт минуту и напрямую влияет на то, каким станет APEX.
                </Typography>

                {/* Star rating */}
                <Typography level="title-sm" sx={{ mb: 1 }}>
                    Как тебе платформа в целом?
                </Typography>
                <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mb: 3 }}>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Box
                            key={n}
                            onClick={() => setRating(n)}
                            onMouseEnter={() => setHover(n)}
                            onMouseLeave={() => setHover(0)}
                            sx={{ cursor: 'pointer', lineHeight: 0, transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.12)' } }}
                        >
                            <Star
                                size={38}
                                weight={(hover || rating) >= n ? 'fill' : 'regular'}
                                color={(hover || rating) >= n ? '#DA7756' : '#C5C5C5'}
                            />
                        </Box>
                    ))}
                </Stack>

                {/* Text */}
                <Box sx={{ textAlign: 'left', mb: 2 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                        Что нравится и что хочется улучшить?
                    </Typography>
                    <Textarea
                        minRows={3}
                        maxRows={6}
                        placeholder="Поделись впечатлениями — это поможет нам стать лучше"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        sx={{ borderRadius: 'lg' }}
                    />
                </Box>

                {/* Consent */}
                <Box sx={{ textAlign: 'left', mb: 3 }}>
                    <Checkbox
                        size="sm"
                        checked={canPublish}
                        onChange={(e) => setCanPublish(e.target.checked)}
                        label="Разрешаю использовать мой отзыв (например, на сайте APEX)"
                        sx={{ alignItems: 'flex-start' }}
                    />
                </Box>

                {error && (
                    <Typography level="body-sm" sx={{ color: '#E03E3E', mb: 1.5 }}>
                        {error}
                    </Typography>
                )}

                {/* Actions */}
                <Stack direction="row" spacing={1.5} justifyContent="center">
                    <Button variant="plain" color="neutral" size="sm" onClick={onClose} disabled={saving}>
                        Позже
                    </Button>
                    <Button
                        variant="solid"
                        onClick={submit}
                        loading={saving}
                        disabled={rating < 1}
                        sx={{ bgcolor: '#DA7756', '&:hover': { bgcolor: '#C4623E' }, px: 3 }}
                    >
                        Отправить отзыв
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}
